<script>
import { browser } from '$app/environment'
import { onMount } from 'svelte'

import { formatCounter, parseCounter } from './counter-animation'

/** @type {{ counterUp?: boolean }} */
let { counterUp = false } = $props()

const COUNTER_DURATION_MS = 2000

const JQUERY =
  'https://d3e54v103j8qbb.cloudfront.net/js/jquery-3.5.1.min.dc5e7f18c8.js?site=6847278788d05d193b48314c'
const JQUERY_INTEGRITY = 'sha256-9/aliU8dGd2tb6OSsuzixeV4y/faTqgFtohetphbbj0='

/**
 * Roll each `.counter` span up from zero to its target value when it scrolls into
 * view. Replaces the legacy jQuery Waypoints + CounterUp CDN plugins, which broke
 * under jQuery 3.x (Waypoints relies on the removed `$(window).load()` shorthand).
 */
function animateCounters() {
  const counters = /** @type {NodeListOf<HTMLElement>} */ (
    document.querySelectorAll('.counter')
  )
  if (!counters.length || typeof IntersectionObserver === 'undefined') return () => {}

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // One canceller per running counter; each cancels that counter's in-flight animation frame.
  const cancellers = new Set()

  const run = (element) => {
    const parsed = parseCounter(element.textContent ?? '')
    if (!parsed) return

    if (prefersReducedMotion) {
      element.textContent = formatCounter(parsed.target, parsed)
      return
    }

    element.textContent = formatCounter(0, parsed)
    let startTime = null
    let rafId = 0
    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / COUNTER_DURATION_MS, 1)
      element.textContent = formatCounter(parsed.target * progress, parsed)
      if (progress < 1) rafId = requestAnimationFrame(step)
    }
    rafId = requestAnimationFrame(step)
    cancellers.add(() => cancelAnimationFrame(rafId))
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        run(/** @type {HTMLElement} */ (entry.target))
        obs.unobserve(entry.target)
      }
    },
    { threshold: 0.25 }
  )

  counters.forEach((counter) => observer.observe(counter))

  return () => {
    observer.disconnect()
    for (const cancel of cancellers) cancel()
    cancellers.clear()
  }
}

function loadScript(src, integrity) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    if (integrity) {
      s.integrity = integrity
      s.crossOrigin = 'anonymous'
    }
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.body.appendChild(s)
  })
}

onMount(() => {
  if (!browser) return

  const html = document.documentElement
  if (!html.className.includes('w-mod-js')) {
    html.className += ' w-mod-js'
  }
  if ('ontouchstart' in window) {
    if (!html.className.includes('w-mod-touch')) {
      html.className += ' w-mod-touch'
    }
  }

  // Counter animation is dependency-free — run it regardless of whether the
  // Webflow jQuery interactions below succeed.
  const disposeCounters = counterUp ? animateCounters() : undefined

  let cancelled = false

  ;(async () => {
    try {
      await loadScript(JQUERY, JQUERY_INTEGRITY)
      if (cancelled) return
      await loadScript('/webflow/js/imx-data-site-redux.js')
    } catch {
      /* Webflow interactions are non-critical if CDN fails */
    }
  })()

  return () => {
    cancelled = true
    disposeCounters?.()
  }
})
</script>
